import { useCallback, useContext, useMemo } from 'react';
import Promise from 'bluebird';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { saveCampaignAndLinkedData, initialBrandExclusionsViewModel, saveAssetGroupAndLinkedData } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { getStoreQueriesWithLocalMock } from '@bingads-webui-campaign-react/merchant-shared';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { createDefaultCampaign, handleDefaultCampaignCreationError } from '../../shared/model/create-default-campaign';
import { getDefaultAssetGroup } from '../../shared/model/default-data';

export const useProductsStepRepository = () => {
  const { state, send } = useWizardService();

  const scenarioContext = useContext(ScenarioContext);

  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { scope } = useContext(ScopeContext) || {};

  const { cid, aid } = scope.values;

  const serverContext = useContext(ServerContext);

  const storeQueries = useMemo(() => getStoreQueriesWithLocalMock(), []);

  const load = useCallback(() => {
    const {
      providerId,
      salesCountry,
      localInventory,
      isFeedFiltered,
      feedLabel,
      campaign: savedCampaign,
      assetGroup: savedAssetGroup,
    } = state.context.wizardData;

    const saveCampaignsPromise = savedCampaign
      ? Promise.resolve(savedCampaign)
      : createDefaultCampaign({
        i18n,
        serverContext,
        scenarioContext,
        providerId,
        salesCountry,
        localInventory,
        isFeedFiltered,
        feedLabel,
      }).then((result) => {
        const { campaign } = result;

        activity.trace('[PmaxNorthStarWizard] - Default Campaign saved');
        activity.signal(`[PmaxNorthStarWizard] - Default Campaign saved, campaignId: ${campaign.data.Id}`);

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: result.campaign.data });

        return result.campaign.data;
      }).catch((errorData) => {
        activity.trace('[PmaxNorthStarWizard] - Default Campaign creation failed', { ...errorData });

        handleDefaultCampaignCreationError({ i18n, scope, errorData });

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ ...errorData });
      });

    const getStoreInfoPromise = storeQueries.getStore(providerId, true);

    const savedAssetGroupsPromise = savedAssetGroup ?
      Promise.resolve(savedAssetGroup) :
      getStoreInfoPromise.then((storeInfo) => {
        const assetGroup = getDefaultAssetGroup({
          businessName: storeInfo && storeInfo.Name,
          i18n,
          storeFinalUrl: storeInfo && storeInfo.DestinationUrl,
        });

        return assetGroup;
      });

    const promises = {
      campaign: saveCampaignsPromise,
      assetGroup: savedAssetGroupsPromise,
    };

    return Promise.props(promises).then(({ campaign, assetGroup }) => ({
      campaign,
      assetGroup,
    })).catch((errorData) => {
      activity.trace('[PmaxNorthStarWizard] - Default Campaign save failed');
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, i18n, scenarioContext, scope, send, serverContext, state.context.wizardData, storeQueries]);

  const save = useCallback(({
    data,
    initialData,
  }) => {
    const {
      campaign: campaignToSave, assetGroup: assetGroupToSave,
    } = data;

    const { assetGroup: savedAssetGroup } = state.context.wizardData;

    const { campaign: initialCampaign, assetGroup: initialAssetGroup } = initialData;

    const saveCampaignPromise = saveCampaignAndLinkedData({
      campaign: { data: campaignToSave },
      originalCampaign: { data: initialCampaign },
      brandExclusions: initialBrandExclusionsViewModel,
    }, {
      scenario: scenarioContext.name,
      i18n,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
    });

    const saveAssetGroupPromise = savedAssetGroup ? saveAssetGroupAndLinkedData({
      assetGroup: { data: assetGroupToSave },
      originalAssetGroup: { data: initialAssetGroup },
      campaign: campaignToSave,
    }, {
      scenario: scenarioContext.name,
      i18n,
      activityLogger: scenarioContext.createActivity('updateAssetGroup'),
      serverContext,
      cid,
      aid,
      activity,
    }) : Promise.resolve({ assetGroup: { data: assetGroupToSave } });

    return Promise.props({
      campaign: saveCampaignPromise,
      assetGroup: saveAssetGroupPromise,
    }).then(({
      campaign: campaignResults,
      assetGroup: assetGroupResults,
    }) => {
      activity.trace('[PmaxNorthStarWizard] - campaign/assetgroup settings updated in product step');
      activity.signal('[PmaxNorthStarWizard] - campaign/assetgroup settings updated in product step');

      const {
        campaign,
      } = campaignResults;

      const {
        assetGroup,
      } = assetGroupResults;

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        campaign: campaign.data,
        assetGroup: assetGroup.data,
        savedProductFilter: assetGroupToSave.productFilter,
        savedListingGroup: assetGroupToSave.listingGroup,
      });

      return {
        campaign: campaign.data,
        assetGroup: assetGroup.data,
      };
    }).catch((errorData) => {
      activity.trace('[PmaxNorthStarWizard] - campaign/assetgroup settings update failed in product step');

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, i18n, scenarioContext, send, serverContext, state.context.wizardData]);

  return {
    load,
    save,
  };
};
